import { useEffect, useRef, useState } from "react";

const OFFSET = { x: -22, y: 18 };
const EASE = 0.055;

const FireflySvg = () => (
  <svg viewBox="0 0 48 48" width="34" height="34" aria-hidden="true">
    <path d="M22 13 C18 7 14 6 12 4 M26 13 C30 7 34 6 36 4" fill="none" stroke="#1C1917" strokeWidth="1.3" strokeLinecap="round" />
    <ellipse cx="15" cy="20" rx="9" ry="5.5" transform="rotate(-28 15 20)" fill="rgba(255,255,255,0.75)" stroke="#1C1917" strokeWidth="1.2" />
    <ellipse cx="33" cy="20" rx="9" ry="5.5" transform="rotate(28 33 20)" fill="rgba(255,255,255,0.75)" stroke="#1C1917" strokeWidth="1.2" />
    <circle cx="24" cy="16" r="4" fill="#fff" stroke="#1C1917" strokeWidth="1.3" />
    <ellipse className="ff-belly" cx="24" cy="29" rx="6.5" ry="9" fill="#FACC15" stroke="#1C1917" strokeWidth="1.3" />
    <path d="M19 27 h10 M19.5 31 h9" stroke="#B45309" strokeWidth="0.9" opacity="0.6" />
  </svg>
);

const useCursorMode = () => {
  const [mode, setMode] = useState("off");
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setMode(reduced.matches ? "off" : fine.matches ? "follow" : "touch");
    update();
    reduced.addEventListener("change", update);
    fine.addEventListener("change", update);
    return () => { reduced.removeEventListener("change", update); fine.removeEventListener("change", update); };
  }, []);
  return mode;
};

const spawnRipple = (layer, x, y) => {
  const r = document.createElement("span");
  r.className = "ff-ripple";
  r.style.left = `${x}px`;
  r.style.top = `${y}px`;
  layer.appendChild(r);
  r.addEventListener("animationend", () => r.remove());
};

export const FireflyCursor = () => {
  const mode = useCursorMode();
  const layerRef = useRef(null);
  const flyRef = useRef(null);

  useEffect(() => {
    if (mode === "off") return undefined;
    const layer = layerRef.current;
    if (mode === "touch") {
      const onTap = (e) => spawnRipple(layer, e.clientX, e.clientY);
      window.addEventListener("pointerdown", onTap, { passive: true });
      return () => window.removeEventListener("pointerdown", onTap);
    }
    const fly = flyRef.current;
    const pos = { x: -100, y: -100 };
    const target = { x: -100, y: -100 };
    let raf = null;
    let seen = false;
    const tick = () => {
      pos.x += (target.x - pos.x) * EASE;
      pos.y += (target.y - pos.y) * EASE;
      fly.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
      raf = Math.abs(target.x - pos.x) + Math.abs(target.y - pos.y) > 0.3 ? requestAnimationFrame(tick) : null;
    };
    const onMove = (e) => {
      target.x = e.clientX + OFFSET.x;
      target.y = e.clientY + OFFSET.y;
      if (!seen) { seen = true; pos.x = target.x; pos.y = target.y; fly.style.opacity = "1"; }
      if (!raf) raf = requestAnimationFrame(tick);
    };
    const onDown = () => {
      fly.classList.remove("ff-burst");
      void fly.offsetWidth;
      fly.classList.add("ff-burst");
      spawnRipple(layer, pos.x, pos.y);
    };
    const onLeave = () => { fly.style.opacity = "0"; seen = false; };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onDown, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, [mode]);

  if (mode === "off") return null;
  return (
    <div ref={layerRef} className="ff-layer" aria-hidden="true" data-testid="firefly-cursor-layer">
      {mode === "follow" && (
        <div ref={flyRef} className="ff-fly" data-testid="firefly-cursor">
          <div className="ff-float"><span className="ff-glow" /><FireflySvg /></div>
        </div>
      )}
    </div>
  );
};
